import React, { useState, useEffect } from 'react';
import { getApprovers, updateApprover } from '../../adminApi';
import { Approver } from '../../adminTypes';

interface ExpandedRow {
  id: number;
  isEditing: boolean;
  editData: {
    approval_limit: number | null;
    allowed_vendor_numbers: string;
    allowed_job_numbers: string;
    notify_on_assignment: boolean;
    notify_on_escalation: boolean;
    active: boolean;
  };
}

const UserManagement: React.FC = () => {
  const [approvers, setApprovers] = useState<Approver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Map<number, ExpandedRow>>(
    new Map()
  );
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchApprovers();
  }, []);

  const fetchApprovers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getApprovers();
      setApprovers(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load approvers'
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpansion = (approver: Approver) => {
    const newExpanded = new Map(expandedRows);

    if (newExpanded.has(approver.id)) {
      newExpanded.delete(approver.id);
    } else {
      newExpanded.set(approver.id, {
        id: approver.id,
        isEditing: true,
        editData: {
          approval_limit: approver.approval_limit || null,
          allowed_vendor_numbers: (approver.allowed_vendor_numbers || []).join(
            ', '
          ),
          allowed_job_numbers: (approver.allowed_job_numbers || []).join(
            ', '
          ),
          notify_on_assignment: approver.notify_on_assignment || false,
          notify_on_escalation: approver.notify_on_escalation || false,
          active: approver.active !== false,
        },
      });
    }

    setExpandedRows(newExpanded);
  };

  const updateEditData = (
    approverId: number,
    field: string,
    value: string | number | boolean | null
  ) => {
    const expanded = expandedRows.get(approverId);
    if (!expanded) return;

    const updated = {
      ...expanded,
      editData: {
        ...expanded.editData,
        [field]: value,
      },
    };

    const newExpanded = new Map(expandedRows);
    newExpanded.set(approverId, updated);
    setExpandedRows(newExpanded);
  };

  const handleSaveApprover = async (approverId: number) => {
    const expanded = expandedRows.get(approverId);
    if (!expanded) return;

    try {
      setSavingIds((prev) => new Set(prev).add(approverId));

      const updates = {
        approval_limit:
          expanded.editData.approval_limit === null
            ? null
            : Number(expanded.editData.approval_limit),
        allowed_vendor_numbers: expanded.editData.allowed_vendor_numbers
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s)
          .map(Number),
        allowed_job_numbers: expanded.editData.allowed_job_numbers
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s),
        notify_on_assignment: expanded.editData.notify_on_assignment,
        notify_on_escalation: expanded.editData.notify_on_escalation,
        active: expanded.editData.active,
      };

      await updateApprover(approverId, updates);

      // Refresh the approvers list
      await fetchApprovers();

      // Clear the expanded row
      const newExpanded = new Map(expandedRows);
      newExpanded.delete(approverId);
      setExpandedRows(newExpanded);
    } catch (err) {
      setError(
        err instanceof Error
          ? `Failed to save approver: ${err.message}`
          : 'Failed to save approver'
      );
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(approverId);
        return next;
      });
    }
  };

  const getRoleBadgeColor = (
    role: string
  ): { bg: string; text: string } => {
    switch (role) {
      case 'ADMIN':
        return { bg: '#00386C', text: '#FFFFFF' };
      case 'APPROVER':
        return { bg: '#22C55E', text: '#FFFFFF' };
      case 'READ_ONLY':
        return { bg: '#9CA3AF', text: '#FFFFFF' };
      default:
        return { bg: '#E5E7EB', text: '#374151' };
    }
  };

  const formatApprovalLimit = (limit: number | null | undefined): string => {
    if (limit === null || limit === undefined) {
      return 'Unlimited';
    }
    return `$${limit.toLocaleString()}`;
  };

  const formatLastLogin = (date: string | null | undefined): string => {
    if (!date) {
      return 'Never';
    }

    const lastLogin = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - lastLogin.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 30) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return lastLogin.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading approvers...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ color: '#00386C', marginBottom: '1rem' }}>
        User Management
      </h1>

      {error && (
        <div
          style={{
            backgroundColor: '#FEE2E2',
            border: '1px solid #FECACA',
            borderRadius: '0.375rem',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#991B1B',
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          backgroundColor: '#EFF6FF',
          border: '1px solid #BFDBFE',
          borderRadius: '0.375rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          fontSize: '0.875rem',
          color: '#1E40AF',
        }}
      >
        <p style={{ margin: 0 }}>
          Users are auto-created on login via Entra SSO. Role is inherited from
          Entra AD groups. Edit portal-specific settings below.
        </p>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            borderRadius: '0.5rem',
            overflow: 'hidden',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#00386C', color: '#FFFFFF' }}>
              <th
                style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                Name
              </th>
              <th
                style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                Email
              </th>
              <th
                style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                Role
              </th>
              <th
                style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                Approval Limit
              </th>
              <th
                style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                Last Login
              </th>
              <th
                style={{
                  padding: '1rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  borderBottom: '1px solid #E5E7EB',
                }}
              >
                Active
              </th>
            </tr>
          </thead>
          <tbody>
            {approvers.map((approver) => {
              const isExpanded = expandedRows.has(approver.id);
              const expanded = expandedRows.get(approver.id);
              const roleBadge = getRoleBadgeColor(approver.role);

              return (
                <React.Fragment key={approver.id}>
                  <tr
                    onClick={() => toggleRowExpansion(approver)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isExpanded ? '#F3F4F6' : '#FFFFFF',
                      borderBottom: '1px solid #E5E7EB',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                          '#F9FAFB';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                          '#FFFFFF';
                      }
                    }}
                  >
                    <td style={{ padding: '1rem', color: '#111827' }}>
                      {approver.display_name}
                    </td>
                    <td style={{ padding: '1rem', color: '#6B7280' }}>
                      {approver.email}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          backgroundColor: roleBadge.bg,
                          color: roleBadge.text,
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                        }}
                      >
                        {approver.role}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', color: '#111827' }}>
                      {formatApprovalLimit(approver.approval_limit)}
                    </td>
                    <td style={{ padding: '1rem', color: '#6B7280' }}>
                      {formatLastLogin(approver.last_login_at)}
                    </td>
                    <td style={{ padding: '1rem', color: '#111827' }}>
                      <input
                        type="checkbox"
                        checked={approver.active !== false}
                        disabled
                        style={{ cursor: 'not-allowed' }}
                      />
                    </td>
                  </tr>

                  {isExpanded && expanded && (
                    <tr style={{ backgroundColor: '#FAFBFC' }}>
                      <td colSpan={6} style={{ padding: '1.5rem' }}>
                        <div
                          style={{
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #E5E7EB',
                            borderRadius: '0.375rem',
                            padding: '1.5rem',
                          }}
                        >
                          <h3
                            style={{
                              margin: '0 0 1rem 0',
                              color: '#00386C',
                              fontSize: '1rem',
                            }}
                          >
                            Edit Settings for {approver.display_name}
                          </h3>

                          <div style={{ display: 'grid', gap: '1.5rem' }}>
                            {/* Approval Limit */}
                            <div>
                              <label
                                style={{
                                  display: 'block',
                                  marginBottom: '0.5rem',
                                  fontWeight: '500',
                                  color: '#374151',
                                }}
                              >
                                Approval Limit (USD)
                              </label>
                              <input
                                type="number"
                                value={
                                  expanded.editData.approval_limit === null
                                    ? ''
                                    : expanded.editData.approval_limit
                                }
                                onChange={(e) =>
                                  updateEditData(
                                    approver.id,
                                    'approval_limit',
                                    e.target.value === ''
                                      ? null
                                      : Number(e.target.value)
                                  )
                                }
                                placeholder="Leave blank for unlimited"
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #D1D5DB',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem',
                                  boxSizing: 'border-box',
                                }}
                              />
                              <p
                                style={{
                                  margin: '0.25rem 0 0 0',
                                  fontSize: '0.75rem',
                                  color: '#6B7280',
                                }}
                              >
                                Current: {formatApprovalLimit(approver.approval_limit)}
                              </p>
                            </div>

                            {/* Allowed Vendor Numbers */}
                            <div>
                              <label
                                style={{
                                  display: 'block',
                                  marginBottom: '0.5rem',
                                  fontWeight: '500',
                                  color: '#374151',
                                }}
                              >
                                Allowed Vendor Numbers
                              </label>
                              <input
                                type="text"
                                value={expanded.editData.allowed_vendor_numbers}
                                onChange={(e) =>
                                  updateEditData(
                                    approver.id,
                                    'allowed_vendor_numbers',
                                    e.target.value
                                  )
                                }
                                placeholder="Comma-separated vendor numbers (e.g., 1000, 2000, 3000)"
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #D1D5DB',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem',
                                  boxSizing: 'border-box',
                                }}
                              />
                              <p
                                style={{
                                  margin: '0.25rem 0 0 0',
                                  fontSize: '0.75rem',
                                  color: '#6B7280',
                                }}
                              >
                                Leave blank to allow all vendors
                              </p>
                            </div>

                            {/* Allowed Job Numbers */}
                            <div>
                              <label
                                style={{
                                  display: 'block',
                                  marginBottom: '0.5rem',
                                  fontWeight: '500',
                                  color: '#374151',
                                }}
                              >
                                Allowed Job Numbers
                              </label>
                              <input
                                type="text"
                                value={expanded.editData.allowed_job_numbers}
                                onChange={(e) =>
                                  updateEditData(
                                    approver.id,
                                    'allowed_job_numbers',
                                    e.target.value
                                  )
                                }
                                placeholder="Comma-separated job numbers (e.g., JOB001, JOB002)"
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  border: '1px solid #D1D5DB',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.875rem',
                                  boxSizing: 'border-box',
                                }}
                              />
                              <p
                                style={{
                                  margin: '0.25rem 0 0 0',
                                  fontSize: '0.75rem',
                                  color: '#6B7280',
                                }}
                              >
                                Leave blank to allow all jobs
                              </p>
                            </div>

                            {/* Notification Settings */}
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns:
                                  'repeat(auto-fit, minmax(300px, 1fr))',
                                gap: '1rem',
                              }}
                            >
                              <label
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={
                                    expanded.editData.notify_on_assignment
                                  }
                                  onChange={(e) =>
                                    updateEditData(
                                      approver.id,
                                      'notify_on_assignment',
                                      e.target.checked
                                    )
                                  }
                                  style={{ cursor: 'pointer' }}
                                />
                                <span style={{ color: '#374151' }}>
                                  Notify on Assignment
                                </span>
                              </label>

                              <label
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={
                                    expanded.editData.notify_on_escalation
                                  }
                                  onChange={(e) =>
                                    updateEditData(
                                      approver.id,
                                      'notify_on_escalation',
                                      e.target.checked
                                    )
                                  }
                                  style={{ cursor: 'pointer' }}
                                />
                                <span style={{ color: '#374151' }}>
                                  Notify on Escalation
                                </span>
                              </label>

                              <label
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={expanded.editData.active}
                                  onChange={(e) =>
                                    updateEditData(
                                      approver.id,
                                      'active',
                                      e.target.checked
                                    )
                                  }
                                  style={{ cursor: 'pointer' }}
                                />
                                <span style={{ color: '#374151' }}>
                                  Active
                                </span>
                              </label>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div
                            style={{
                              display: 'flex',
                              gap: '0.75rem',
                              marginTop: '1.5rem',
                            }}
                          >
                            <button
                              onClick={() =>
                                handleSaveApprover(approver.id)
                              }
                              disabled={savingIds.has(approver.id)}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#00386C',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                cursor: savingIds.has(approver.id)
                                  ? 'not-allowed'
                                  : 'pointer',
                                opacity: savingIds.has(approver.id) ? 0.7 : 1,
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                if (!savingIds.has(approver.id)) {
                                  (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                                    '#002855';
                                }
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                                  '#00386C';
                              }}
                            >
                              {savingIds.has(approver.id)
                                ? 'Saving...'
                                : 'Save Changes'}
                            </button>

                            <button
                              onClick={() => toggleRowExpansion(approver)}
                              disabled={savingIds.has(approver.id)}
                              style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: '#E5E7EB',
                                color: '#374151',
                                border: 'none',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                cursor: savingIds.has(approver.id)
                                  ? 'not-allowed'
                                  : 'pointer',
                                opacity: savingIds.has(approver.id) ? 0.7 : 1,
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                if (!savingIds.has(approver.id)) {
                                  (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                                    '#D1D5DB';
                                }
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                                  '#E5E7EB';
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {approvers.length === 0 && !error && (
        <div
          style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#6B7280',
          }}
        >
          <p>No approvers found. Users will appear after their first login.</p>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
